import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import type { Observable} from 'rxjs';
import { ReplaySubject } from 'rxjs';
import { environment } from '../../../../environments/environment';

const FB_SDK_SCRIPT_ID = 'facebook-jssdk';
const FB_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const FB_GRAPH_VERSION = 'v25.0';

export interface FacebookLoginAuthResponse {
  readonly code?: string;
  readonly accessToken?: string;
  readonly userID?: string;
}

export interface FacebookLoginResponse {
  readonly authResponse?: FacebookLoginAuthResponse | null;
  readonly status?: string;
}

export interface FacebookLoginOptions {
  readonly config_id?: string;
  readonly response_type?: 'code' | 'token';
  readonly override_default_response_type?: boolean;
  readonly extras?: Record<string, unknown>;
  readonly scope?: string;
}

export interface FacebookSDK {
  init(config: Record<string, unknown>): void;
  login(
    callback: (response: FacebookLoginResponse) => void,
    options: FacebookLoginOptions,
  ): void;
}

interface WindowWithFB extends Window {
  FB?: FacebookSDK;
  fbAsyncInit?: () => void;
}

@Injectable({ providedIn: 'root' })
export class FacebookSdkLoader {
  private readonly document = inject(DOCUMENT);
  private ready$: ReplaySubject<FacebookSDK> | null = null;
  /** Instância já inicializada (ou null). Permite chamar `FB.login` SÍNCRONO dentro do gesto. */
  private fb: FacebookSDK | null = null;

  private readonly _ready = signal(false);
  /** `true` quando o SDK já está baixado e inicializado (FB.init concluído). */
  readonly ready = this._ready.asReadonly();

  /**
   * Aquece o SDK sem bloquear (fire-and-forget). Chamar cedo (ex.: ao abrir o dialog) para que,
   * quando o usuário clicar em conectar, `FB.login` rode síncrono no gesto → popup em vez de aba.
   */
  preload(): void {
    this.load().subscribe({ error: () => undefined });
  }

  /** Instância pronta do SDK, ou null se ainda carregando. Para login síncrono no gesto. */
  peek(): FacebookSDK | null {
    return this.fb;
  }

  load(): Observable<FacebookSDK> {
    if (this.ready$) return this.ready$.asObservable();

    this.ready$ = new ReplaySubject<FacebookSDK>(1);
    const win = this.document.defaultView as WindowWithFB | null;
    if (!win) {
      this.ready$.error(new Error('No browser context available.'));
      return this.ready$.asObservable();
    }

    if (win.FB) {
      this.initAndEmit(win.FB);
      return this.ready$.asObservable();
    }

    win.fbAsyncInit = () => {
      if (win.FB) this.initAndEmit(win.FB);
    };

    if (!this.document.getElementById(FB_SDK_SCRIPT_ID)) {
      const script = this.document.createElement('script');
      script.id = FB_SDK_SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = FB_SDK_SRC;
      script.onerror = () => this.ready$?.error(new Error('Failed to load Facebook SDK.'));
      this.document.head.appendChild(script);
    }

    return this.ready$.asObservable();
  }

  private initAndEmit(fb: FacebookSDK): void {
    fb.init({
      appId: environment.metaAppId,
      autoLogAppEvents: true,
      xfbml: true,
      version: FB_GRAPH_VERSION,
    });
    this.fb = fb;
    this._ready.set(true);
    this.ready$?.next(fb);
    this.ready$?.complete();
  }
}
